import { apiClient } from './apiClient';

export const AUDIT_ACTIONS = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  SUBMIT: 'submit',
  APPROVE: 'approve',
  REJECT: 'reject',
  UPLOAD: 'upload',
  EMAIL_SENT: 'email_sent',
  LOGIN: 'login',
  LOGOUT: 'logout',
} as const;

export const AUDIT_ENTITIES = {
  SESSION: 'session',
  INSPECTION_REPORT: 'inspection_report',
  PROPERTY: 'property',
  CONTROLLER: 'controller',
  ZONE: 'zone',
  DOUBLE_CHECK: 'double_check',
  WATER_METER: 'water_meter',
  USER: 'user',
  WORK_CODE: 'work_code',
  ZONE_TYPE: 'zone_type',
  MANUFACTURER: 'manufacturer',
  REPORT_STATUS: 'report_status',
  BRAND_SETTINGS: 'brand_settings',
  REPORT_STYLE: 'report_style',
  INTEGRATION: 'integration',
  FEEDBACK: 'feedback',
  PHOTO: 'photo',
  THEME: 'theme',
} as const;

export type AuditAction = typeof AUDIT_ACTIONS[keyof typeof AUDIT_ACTIONS];
export type AuditEntity = typeof AUDIT_ENTITIES[keyof typeof AUDIT_ENTITIES];

const SENSITIVE_FIELDS = ['secret', 'password', 'key', 'token', 'connection_string'];

function redactSensitiveValue(fieldName: string): boolean {
  const lower = fieldName.toLowerCase();
  return SENSITIVE_FIELDS.some(s => lower.includes(s));
}

export function computeChanges(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): Record<string, { old: unknown; new: unknown }> | null {
  const changes: Record<string, { old: unknown; new: unknown }> = {};

  const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const key of allKeys) {
    const oldVal = before[key];
    const newVal = after[key];
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      if (redactSensitiveValue(key)) {
        changes[key] = { old: '[redacted]', new: '[changed]' };
      } else {
        changes[key] = { old: oldVal ?? null, new: newVal ?? null };
      }
    }
  }

  return Object.keys(changes).length > 0 ? changes : null;
}

interface AuditLogParams {
  action: AuditAction;
  entityType: AuditEntity;
  entityId?: string | null;
  entityLabel?: string | null;
  parentEntityType?: AuditEntity | null;
  parentEntityId?: string | null;
  parentEntityLabel?: string | null;
  details?: Record<string, unknown> | null;
  pageContext?: string | null;
}

export async function logAudit(params: AuditLogParams): Promise<void> {
  try {
    await apiClient.post('/api/audit-logs', {
      action: params.action,
      entity_type: params.entityType,
      entity_id: params.entityId ?? null,
      entity_label: params.entityLabel ?? null,
      parent_entity_type: params.parentEntityType ?? null,
      parent_entity_id: params.parentEntityId ?? null,
      parent_entity_label: params.parentEntityLabel ?? null,
      details: params.details ?? null,
      page_context: params.pageContext ?? null,
    });
  } catch (err) {
    console.error('Audit log failed:', err);
  }
}
